import { PageContainer, PageSectionTitle, Tile } from 'components/Layout/PageContainer';
import { Service, useProDataService } from 'services/data.service';
import { useDataRequest } from 'hooks/useDataRequest';
import { MetricsLoadingSpinner } from 'components/MetricsLoadingSpinner';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useInfoDrawer } from 'context/InfoDrawerContext';
import { ReportLinkCard } from 'pages/insights-pro/common/reports/ReportLink';
import { FormattedMessage, useIntl } from 'react-intl';
import { Measure, useModelService } from 'services/model/model.service';
import { MeasureCardRow } from 'components/MeasureCardRow';
import { TimeSeriesChartPageSection } from 'components/chart/TimeSeriesChartPageSection';
import { LineChartRenderer } from 'components/chart/renderer/LineChartRenderer';
import { createTimeSeriesChartConfig } from 'utils/createTimeSeriesChartConfig';
import { useDatePickerLabels } from 'hooks/useDatePickerLabels';
import { TenantConfigContext } from 'context/TenantConfigContext';
import { useDataServiceQueryResult } from 'hooks/useDataServiceQueryResult';
import { ProductDashboardInfoContent } from './components/ProductDashboardInfoContent';
import { ProductsList } from './components/ProductsList';

export function ProductDashboard() {
  const intl = useIntl();
  const { fetchData } = useProDataService(Service.Products);
  const { setContent } = useInfoDrawer();
  const labelContext = useDatePickerLabels();
  const requestData = useDataRequest();
  const { getMeasures, getAttribute } = useModelService();
  const [netSlsQty, netSlsGmvVal, retQty, retGmvVal, ordQty, itemNetSlsGmvValOD] = getMeasures([
    'netSlsQtyExpOD',
    'netSlsGmvValExpOD',
    'retQtyRateExpOD',
    'retGmvValExpOD',
    'ordQty',
    'itemNetSlsGmvValExpOD',
  ]);
  const productMeasures: Measure[] = useMemo(
    () => [ordQty, retQty, netSlsQty, itemNetSlsGmvValOD],
    [ordQty, retQty, netSlsQty, itemNetSlsGmvValOD]
  );

  const [productsTimeSeriesMeasure, setProductsTimeSeriesMeasure] = useState<Measure>(productMeasures[0]);

  const dashboardState = useDataServiceQueryResult(
    requestData,
    data => fetchData('dashboard', data),
    data => data,
    ['insights-products-dashboard']
  );
  const topSellerState = useDataServiceQueryResult(
    requestData,
    data => fetchData(`reports/top-sellers/${netSlsQty.code}`, data),
    response => ({ response }),
    ['insights-products-top-sellers', netSlsQty.code]
  );
  const topReturnState = useDataServiceQueryResult(
    requestData,
    data => fetchData(`reports/returns/${retQty.code}`, data),
    response => ({ response }),
    ['insights-products-returns-dashboard-tile', retQty.code]
  );

  const timeSeriesState = useDataServiceQueryResult(
    requestData,
    data => fetchData(`dashboard/time-series/${productsTimeSeriesMeasure.code}`, data),
    (response, request) =>
      response &&
      new LineChartRenderer(
        createTimeSeriesChartConfig({
          getAttribute,
          measures: [productsTimeSeriesMeasure],
          request,
          response,
          intl,
          labelContext,
          groupByAttribute: request.groupByAttribute,
        })
      ),
    ['insights-products-time-series', productsTimeSeriesMeasure.code, intl.locale]
  );

  const timeSeriesProductsMeasuresChangeHandler = (measure: Measure) => () => {
    if (measure.code !== productsTimeSeriesMeasure.code) {
      setProductsTimeSeriesMeasure(measure);
    }
  };

  const tenantContext = useContext(TenantConfigContext);
  const currentAccountNumber = tenantContext.accountNumber;

  const [topSellerDataAccountNumber, setTopSellerDataAccountNumber] = useState<string | null>(null);
  const [topReturnDataAccountNumber, setTopReturnDataAccountNumber] = useState<string | null>(null);

  useEffect(() => {
    if (topSellerState.data) {
      setTopSellerDataAccountNumber(currentAccountNumber);
    }
  }, [topSellerState.data, currentAccountNumber]);

  useEffect(() => {
    if (topReturnState.data) {
      setTopReturnDataAccountNumber(currentAccountNumber);
    }
  }, [topReturnState.data, currentAccountNumber]);

  const isTopSellerDataInSyncWithTenant = topSellerDataAccountNumber === currentAccountNumber;
  const isTopReturnDataInSyncWithTenant = topReturnDataAccountNumber === currentAccountNumber;

  useEffect(() => {
    const productDashboardInfo = {
      titleKey: 'insights.product_tab.title',
      subtitleKey: 'info.content.all_dates_utc',
      content: <ProductDashboardInfoContent />,
    };
    setContent(productDashboardInfo);
    return () => {
      setContent(null);
    };
  }, [netSlsQty, netSlsGmvVal, ordQty, retQty, setContent]);

  useEffect(() => {
    const measure = productMeasures.find(m => productsTimeSeriesMeasure.code === m.code);
    setProductsTimeSeriesMeasure(measure!);
  }, [intl, productMeasures, productsTimeSeriesMeasure.code]);

  return (
    <PageContainer className="products">
      <Tile
        isError={topSellerState.isError}
        titleId="product.topsellers"
        additionalTitle={intl.formatMessage({
          id: 'insights.product_tab.by_net_sold_items',
          defaultMessage: 'by Net Sold Items',
        })}
        className="xl:col-span-6"
        errorDetails={topSellerState.error}
      >
        <ProductsList
          list={isTopSellerDataInSyncWithTenant ? topSellerState.data?.response.sets.products : undefined}
          measurePrimary={netSlsQty}
          measureSecondary={netSlsGmvVal}
          comparisonActive={dashboardState.data?.compareValues !== undefined}
          testId="top-selling-products"
        />
      </Tile>
      <Tile
        isError={topReturnState.isError}
        titleId="product.topreturns"
        additionalTitle={intl.formatMessage({
          id: 'product.topreturns.subtitle',
        })}
        className="xl:col-span-6"
        errorDetails={topReturnState.error}
      >
        <ProductsList
          list={isTopReturnDataInSyncWithTenant ? topReturnState.data?.response.sets.products : undefined}
          measurePrimary={retQty}
          measureSecondary={retGmvVal}
          comparisonActive={dashboardState.data?.compareValues !== undefined}
          testId="top-returning-products"
        />
      </Tile>
      <ReportLinkCard
        linkUrl="reports/top-selling"
        titleId="insights.product.reports.top_selling"
        descriptionId="insights.product.reports.top_selling.description"
        className="xl:col-span-6"
      />
      <ReportLinkCard
        linkUrl="reports/returns"
        titleId="insights.product.reports.returns"
        descriptionId="insights.product.reports.returns.description"
        className="xl:col-span-6"
      />
      <PageSectionTitle>
        <FormattedMessage id="products.salesReturnsOverview.title" defaultMessage="Performance Metrics" />
      </PageSectionTitle>
      {dashboardState.isError || timeSeriesState.isError ? (
        <Tile
          errorDetails={dashboardState.error ?? timeSeriesState.error}
          testId="insights-products-performance-error"
        />
      ) : dashboardState.data ? (
        <>
          <MeasureCardRow
            measures={productMeasures}
            testid="measure-card-set-1"
            totalsData={dashboardState.data}
            selectedMeasureCode={productsTimeSeriesMeasure.code}
            onMeasureSelect={timeSeriesProductsMeasuresChangeHandler}
          />
          <TimeSeriesChartPageSection
            timeSeriesMeasure={productsTimeSeriesMeasure}
            timeSeriesRenderer={timeSeriesState.data}
          />
        </>
      ) : (
        <MetricsLoadingSpinner />
      )}
    </PageContainer>
  );
}
